/*
  Variation swatches for the Premium Rail Rubber cards.

  A brand that sells the same cushion profile in several grades used to be
  several cards standing side by side, which read as several products. Each
  brand is now ONE card whose swatches switch grade: Artemis K66 / K55 /
  No.37, Klematch P59 / P37.

  WHY THE WHOLE SWAP IS ONE FRAME

  A swatch changes six things at once - photo, model, price, badge, button
  label and every link on the card. Written naively they land in whatever
  order the browser gets to them, and the card visibly retypes itself: the
  new name over the old price, the old photo under the new badge. The McDermott
  tiles hit exactly that and it is why they are done this way.

  So nothing is written until the incoming photo has actually decoded
  (img.decode(), not onload - decode resolves when the bitmap is ready to
  paint, which is the moment that matters), and then every field is written
  inside a single requestAnimationFrame. One frame in, one frame out; the
  crossfade is CSS on top of that.

  RACE PROTECTION

  Pointing along a swatch strip starts a swap per swatch and their decodes
  finish out of order - the third can resolve before the first. Each swap
  takes a sequence number on the group; both after the decode and inside the
  frame it checks it is still the newest. A stale swap returns without
  writing, so the card always lands on the swatch the pointer finished on and
  never on whichever image happened to decode last.

  All photos are in the DOM from the start, stacked and opacity-0. Nothing is
  fetched on hover, so a swatch that has been shown once switches instantly.
*/
(function () {
  'use strict';

  // One instance. The row's markup carries its own script tag for the
  // non-lazy path, and although a tag inserted through innerHTML never
  // executes, nothing should depend on that staying true.
  if (window.BCRailRubberVariations) return;

  var GROUP = '[data-rr-group]';
  var SWATCH = '[data-rr-swatch]';

  /*
    WHAT THE PRESS DOES IMMEDIATELY, AND WHAT IT DOES LATER.

    The swap of photo/model/price/badge/button waits for the incoming
    photo to decode, so those six land together in one frame. The CHIP is
    not part of that: it is the control the finger is on, and it moves at
    once. Without this the card did nothing at all for as long as the
    decode took - up to the 400ms cap on a cold photo - which reads as a
    press that missed, so people press again.

    And pressing again used to make it WORSE: every call bumped the
    sequence and started a fresh decode, so a second press on the same
    chip threw away the first press's progress and restarted the wait.
    `pending` is what stops that - a press for the key already on its way
    is not a new swap.
  */
  var pending = (typeof WeakMap === 'function') ? new WeakMap() : null;

  function pendingKey(group) {
    return pending ? pending.get(group) : group.getAttribute('data-rr-pending');
  }

  function setPending(group, key) {
    if (pending) { if (key == null) pending['delete'](group); else pending.set(group, key); }
    else if (key == null) group.removeAttribute('data-rr-pending');
    else group.setAttribute('data-rr-pending', key);
  }

  function markActive(group, key) {
    var swatches = group.querySelectorAll(SWATCH);
    for (var i = 0; i < swatches.length; i++) {
      var on = swatches[i].getAttribute('data-rr-key') === key;
      swatches[i].classList.toggle('is-active', on);
      swatches[i].setAttribute('aria-selected', on ? 'true' : 'false');
      swatches[i].setAttribute('tabindex', on ? '0' : '-1');
    }
  }

  /*
    Warm the photos when the card comes into view.

    They are loading="lazy" and the row sits well down the page, so
    without this the first press on every chip is waiting on the NETWORK,
    not on a decode - measured on the preview, an untouched card reports
    complete=false and naturalWidth=0 on all of them. Warming on approach
    means the decode the swap waits for is already done by the time
    anyone presses, so the swap is both atomic and immediate.

    Nothing is fetched for a card the reader never reaches.
  */
  function warm(group) {
    if (group.getAttribute('data-rr-warm') === '1') return;
    group.setAttribute('data-rr-warm', '1');
    var photos = group.querySelectorAll('[data-rr-photo]');
    for (var i = 0; i < photos.length; i++) photos[i].setAttribute('loading', 'eager');
  }

  function watchForWarmth(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var groups = scope.querySelectorAll(GROUP);
    if (!groups.length) return;
    if (!window.IntersectionObserver) {
      for (var i = 0; i < groups.length; i++) warm(groups[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        warm(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '600px 0px' });
    for (var j = 0; j < groups.length; j++) io.observe(groups[j]);
  }

  function byKey(list, key) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].getAttribute('data-rr-key') === key) return list[i];
    }
    return null;
  }

  /*
    Fit the model name into the two lines the card reserves, by MEASURING
    it rather than counting its characters: the same name fits or does not
    depending on the width the row happens to be at, and this row is five
    equal columns of whatever the lane is.

    Two notches, then stop - a third would be smaller than the price
    sitting next to it, and no name in this row needs one. Nothing is ever
    truncated; the point of the step is that the whole name reads.
  */
  var STEPS = ['is-long', 'is-longer'];

  function fitName(el) {
    if (!el) return;
    el.classList.remove('is-long', 'is-longer');
    for (var i = 0; i < STEPS.length; i++) {
      var lh = parseFloat(window.getComputedStyle(el).lineHeight);
      // line-height computes to a number here; if it ever says "normal"
      // there is nothing to measure against, so leave the name alone.
      if (!lh) return;
      if (el.scrollHeight <= (lh * 2) + 1) return;
      el.classList.add(STEPS[i]);
    }
  }

  /*
    The BRAND is a different problem from the model name above: it is one
    word on one line, and when it does not fit it is silently CUT, because
    the header clips its overflow. Measured across the row: KLEMATCH and
    all three CHAMPIONSHIP cards were losing their last letters, which
    reads exactly like the name disappearing under the price sitting to
    its right - though the two boxes never overlap, there is 10px between
    them.

    So it is fitted the same way the McDermott labels are: stepped down
    half a pixel at a time until it stops overflowing its own box, floored
    at 70% of the declared size. Not abbreviated, not ellipsed - the whole
    word stays readable.
  */
  function fitOneLine(el) {
    if (!el) return;
    el.style.fontSize = '';
    var base = parseFloat(window.getComputedStyle(el).fontSize);
    if (!base) return;
    // nowrap is worn only while measuring: a wrapped element reports no
    // overflow, but leaving it nowrap is what turns a name the step
    // cannot save into a cut one.
    var hadWhiteSpace = el.style.whiteSpace;
    el.style.whiteSpace = 'nowrap';
    var size = base;
    var guard = 0;
    while (el.scrollWidth > el.clientWidth + 0.5 && size > base * 0.7 && guard++ < 40) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
    el.style.whiteSpace = hadWhiteSpace;
  }

  function refit(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var names = scope.querySelectorAll('[data-rr-group] [data-rr-model]');
    for (var i = 0; i < names.length; i++) fitName(names[i]);
    var brands = scope.querySelectorAll('[data-rr-group] .rail-rubber-card__brand');
    for (var j = 0; j < brands.length; j++) fitOneLine(brands[j]);
  }

  function commit(group, swatch, key) {
    var photos = group.querySelectorAll('[data-rr-photo]');
    var swatches = group.querySelectorAll(SWATCH);
    var i;

    var model = group.querySelector('[data-rr-model]');
    var ghost = group.querySelector('[data-rr-price-ghost]');
    var live = group.querySelector('[data-rr-price]');
    var badge = group.querySelector('[data-rr-badge]');
    var buy = group.querySelector('[data-rr-buy]');
    var links = group.querySelectorAll('[data-rr-link]');

    /*
      The payload lives on the swatch under data-rr-v-*, deliberately NOT
      under the same names the targets are marked with. It shipped once as
      data-rr-badge on both, and since the swatch strip is above the badge
      in the card, querySelector('[data-rr-badge]') found the SWATCH first
      and the first press retyped a swatch label to "Set of 6".
    */
    var d = swatch.dataset;

    if (model) {
      model.textContent = d.rrVModel || '';
      // Still inside the one frame, so the reader never sees the name at
      // the wrong size on its way to the right one.
      fitName(model);
    }
    if (ghost) ghost.textContent = d.rrVPricePlain || '';
    /*
      innerHTML, not textContent: the money filter wraps the figure in
      span.hidePrice, the wholesale app's hook. Writing the text alone would
      strip it and the price would stay visible to wholesale customers.
    */
    if (live && d.rrVPriceHtml != null) live.innerHTML = d.rrVPriceHtml;
    if (badge) {
      badge.textContent = d.rrVBadge || '';
      badge.hidden = !d.rrVBadge;
    }
    if (buy) {
      buy.textContent = d.rrVCta || '';
      buy.setAttribute('aria-label', d.rrVCta || '');
      if (d.rrVUrl) buy.setAttribute('href', d.rrVUrl);
    }
    /*
      An out-of-stock size keeps its chip, so selecting one has to be a
      state the card can be in: BUY gives way to the shared grey SOLD OUT
      block. Both live in the markup and only their hidden attribute
      moves, so the card is the same height either way.
    */
    var soldOut = group.querySelector('[data-rr-soldout]');
    var available = d.rrVAvailable !== 'false';
    if (buy) buy.hidden = !available;
    if (soldOut) soldOut.hidden = available;
    for (i = 0; i < links.length; i++) {
      if (d.rrVUrl) links[i].setAttribute('href', d.rrVUrl);
    }

    for (i = 0; i < photos.length; i++) {
      photos[i].classList.toggle('is-active', photos[i].getAttribute('data-rr-key') === key);
    }
    for (i = 0; i < swatches.length; i++) {
      var on = swatches[i] === swatch;
      swatches[i].classList.toggle('is-active', on);
      swatches[i].setAttribute('aria-selected', on ? 'true' : 'false');
      swatches[i].setAttribute('tabindex', on ? '0' : '-1');
    }

    group.setAttribute('data-rr-active', key);
  }

  function select(group, key) {
    if (!group || !key) return;
    // Already showing it, and nothing on the way to change that.
    if (group.getAttribute('data-rr-active') === key && pendingKey(group) == null) return;
    // Already on its way. A second press must not restart the decode.
    if (pendingKey(group) === key) return;
    var swatch = byKey(group.querySelectorAll(SWATCH), key);
    if (!swatch) return;

    // The control answers now; the content follows when it can be painted.
    setPending(group, key);
    markActive(group, key);
    warm(group);

    var seq = (parseInt(group.getAttribute('data-rr-seq') || '0', 10) || 0) + 1;
    group.setAttribute('data-rr-seq', String(seq));
    var current = function () { return group.getAttribute('data-rr-seq') === String(seq); };

    var photo = byKey(group.querySelectorAll('[data-rr-photo]'), key);
    var ready;
    if (photo && typeof photo.decode === 'function') {
      /*
        Normally this settles in about a millisecond - the photo is already
        in the DOM and decoded - and that is the whole point: the card does
        not retype itself until the incoming image can be painted.

        The cap is for the one case where it cannot. These images are
        loading="lazy" and the row sits well down the page, so a swatch
        hovered in the same moment the row scrolls into view is waiting on
        the NETWORK, not on a decode: measured on the preview, an untouched
        card's photos report complete=false and naturalWidth=0, and decode()
        simply does not settle. Without a cap the card would sit frozen for
        as long as that took. 400ms is far past a warm decode and short
        enough that nobody reads it as broken.
      */
      ready = Promise.race([
        photo.decode().catch(function () {}),
        new Promise(function (resolve) { setTimeout(resolve, 400); })
      ]);
    } else {
      ready = Promise.resolve();
    }

    Promise.all([ready]).then(function () {
      if (!current()) return;
      requestAnimationFrame(function () {
        if (!current()) return;
        commit(group, swatch, key);
        // Only the swap that actually landed clears the flag; a stale one
        // returned above and left it to whichever press overtook it.
        if (pendingKey(group) === key) setPending(group, null);
      });
    });
  }

  function groupOf(el) {
    return el.closest ? el.closest(GROUP) : null;
  }

  /*
    CHOSEN IS NOT THE SAME AS SHOWN.

    data-rr-active is what the card shows; data-rr-chosen is what the
    customer picked - by a click, a tap or the keyboard. A hover shows a
    grade without choosing it, and leaving the swatch strip goes back to the
    chosen one. Until 2026-09-10 a hover chose: pointing along the strip on
    the way to BUY left the card on whichever grade the pointer crossed last,
    which is the bug the other swatch rows had in a milder form. The first
    grade the card renders in counts as chosen until something is picked.
  */
  function chosenKey(group) {
    if (!group.hasAttribute('data-rr-chosen')) group.setAttribute('data-rr-chosen', group.getAttribute('data-rr-active') || '');
    return group.getAttribute('data-rr-chosen');
  }

  function choose(group, key) {
    if (!group || !key) return;
    group.setAttribute('data-rr-chosen', key);
    select(group, key);
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target.closest ? event.target.closest(SWATCH) : null;
    if (!swatch) return;
    // The card is clickable as a whole (bc-card-link); a swatch is not a link.
    event.preventDefault();
    event.stopPropagation();
    choose(groupOf(swatch), swatch.getAttribute('data-rr-key'));
  });

  /*
    Hover only where hovering is real. A phone reports no hover, and binding
    mouseover there would fire the synthesised one that follows a tap and
    fight the click above.

    The strip is the unit, as on the cloth rows (assets/bc-hover-preview.js):
    moving from one swatch to the next, or across the gap between them, is a
    swap from preview to preview; leaving the strip for BUY, the photo or
    anything else puts the chosen grade back.
  */
  var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
  if (canHover) {
    document.addEventListener('mouseover', function (event) {
      var swatch = event.target.closest ? event.target.closest(SWATCH) : null;
      if (!swatch) return;
      var group = groupOf(swatch);
      if (!group) return;
      chosenKey(group);
      select(group, swatch.getAttribute('data-rr-key'));
    });

    // The one revert: the pointer leaves the strip - its own pointerleave,
    // served by bc-hover-preview.js for every row (loaded ahead of this
    // file by the same host). The gaps between swatches are the strip's
    // own box, so nothing reverts inside it.
    var HPz = window.BCHoverPreview;
    if (HPz && HPz.onZoneLeave) {
      HPz.onZoneLeave('[data-rr-swatches]', function (strip) {
        var group = groupOf(strip);
        if (!group || !group.hasAttribute('data-rr-chosen')) return;
        select(group, group.getAttribute('data-rr-chosen'));
      });
    }
  }

  document.addEventListener('keydown', function (event) {
    var swatch = event.target.closest ? event.target.closest(SWATCH) : null;
    if (!swatch) return;
    var group = groupOf(swatch);
    if (!group) return;
    var all = Array.prototype.slice.call(group.querySelectorAll(SWATCH));
    var at = all.indexOf(swatch);
    var to = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') to = at + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') to = at - 1;
    else if (event.key === 'Home') to = 0;
    else if (event.key === 'End') to = all.length - 1;
    else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(group, swatch.getAttribute('data-rr-key'));
      return;
    } else return;
    if (to < 0 || to >= all.length) return;
    event.preventDefault();
    all[to].focus();
    // Arrows on a tablist select as they move, so the keyboard chooses.
    choose(group, all[to].getAttribute('data-rr-key'));
  });

  /*
    The card renders its opening state in Liquid, so the first name on
    screen has never been through commit() and has to be fitted here. The
    row is fetched in after load, so shopify:section:load is the signal -
    the cloth host synthesises one on the injected root, since the real
    event is Theme Editor only.

    Refitting on resize as well: the step depends on the column width, and
    five 1fr columns change with every pixel of the viewport.
  */
  function boot() { refit(document); watchForWarmth(document); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (event) {
    var root = event && event.target;
    refit(root);
    watchForWarmth(root);
  });

  var refitTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(refitTimer);
    refitTimer = window.setTimeout(boot, 150);
  }, { passive: true });

  window.BCRailRubberVariations = { select: select, refit: refit };
})();
